import { useEffect, useRef } from "react";
import type { CosmicCode } from "@/mocks/mandaraCodes";

interface Props {
  code: CosmicCode | null;
  shopifyUrl: string;
  onClose: () => void;
}

export default function CosmicModal({ code, shopifyUrl, onClose }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!code) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [code]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  if (!code) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(4,3,6,0.92)", backdropFilter: "blur(12px)" }}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      {/* Outer glow ring */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${code.glowColor} 0%, transparent 70%)`,
          filter: "blur(80px)",
        }}
      />

      {/* Modal panel */}
      <div
        className="relative w-full max-w-lg mx-auto"
        style={{
          background: "linear-gradient(135deg, #0e0b12 0%, #080608 100%)",
          border: `1px solid ${code.auraColor}25`,
          boxShadow: `0 0 80px ${code.glowColor}, inset 0 0 40px rgba(0,0,0,0.5)`,
          animation: "modalReveal 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        }}
      >
        {/* Top accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${code.auraColor}60, transparent)` }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 cursor-pointer whitespace-nowrap w-8 h-8 flex items-center justify-center"
          style={{ color: `${code.auraColor}50`, transition: "color 0.3s" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = code.auraColor; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = `${code.auraColor}50`; }}
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="p-10 md:p-12">
          {/* Code number */}
          <div
            className="text-xs tracking-[0.5em] uppercase mb-6"
            style={{ color: `${code.auraColor}40` }}
          >
            Cosmic Code · {String(code.number).padStart(2, "0")}
          </div>

          {/* Symbol */}
          <div
            className="text-7xl mb-4 leading-none"
            style={{
              color: code.auraColor,
              textShadow: `0 0 40px ${code.glowColor}, 0 0 80px ${code.glowColor}`,
              fontFamily: "serif",
            }}
          >
            {code.symbol}
          </div>

          {/* Name */}
          <h2
            className="text-3xl font-light text-white mb-1"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.06em" }}
          >
            {code.name}
          </h2>
          <div
            className="text-sm mb-8"
            style={{ color: `${code.auraColor}70`, letterSpacing: "0.2em" }}
          >
            {code.nameJp}
          </div>

          {/* Divider */}
          <div
            className="w-8 h-px mb-8"
            style={{ background: `${code.auraColor}40` }}
          />

          {/* Meaning */}
          <p
            className="text-white/50 text-sm leading-relaxed mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem", lineHeight: "1.9" }}
          >
            {code.meaning}
          </p>

          {/* Info grid */}
          <div className="space-y-5 mb-10">
            <div>
              <div
                className="text-xs tracking-[0.4em] uppercase mb-2"
                style={{ color: `${code.auraColor}40` }}
              >
                Emotional State
              </div>
              <div
                className="text-sm"
                style={{ color: `${code.auraColor}80`, fontFamily: "'Cormorant Garamond', serif", fontSize: "0.95rem" }}
              >
                {code.emotionalState}
              </div>
            </div>

            <div>
              <div
                className="text-xs tracking-[0.4em] uppercase mb-2"
                style={{ color: `${code.auraColor}40` }}
              >
                Intention
              </div>
              <div
                className="text-sm italic"
                style={{ color: "rgba(255,255,255,0.45)", fontFamily: "'Cormorant Garamond', serif", fontSize: "0.95rem" }}
              >
                "{code.intention}"
              </div>
            </div>

            <div
              className="p-4"
              style={{
                background: `${code.glowColor}`,
                border: `1px solid ${code.auraColor}15`,
              }}
            >
              <div
                className="text-xs tracking-[0.4em] uppercase mb-2"
                style={{ color: `${code.auraColor}50` }}
              >
                This is for you if…
              </div>
              <div
                className="text-sm"
                style={{ color: "rgba(255,255,255,0.5)", fontFamily: "'Cormorant Garamond', serif", fontSize: "0.9rem", lineHeight: "1.7" }}
              >
                {code.forYouIf}
              </div>
            </div>
          </div>

          {/* CTA */}
          <a
            href={shopifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center cursor-pointer whitespace-nowrap py-4"
            style={{
              border: `1px solid ${code.auraColor}50`,
              color: code.auraColor,
              letterSpacing: "0.4em",
              fontSize: "0.7rem",
              textTransform: "uppercase",
              transition: "all 0.6s ease",
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = `${code.glowColor}`;
              el.style.boxShadow = `0 0 30px ${code.glowColor}`;
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = "transparent";
              el.style.boxShadow = "none";
            }}
          >
            このコードを纏う
          </a>
        </div>

        {/* Bottom accent line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${code.auraColor}30, transparent)` }}
        />
      </div>

      <style>{`
        @keyframes modalReveal {
          from { opacity: 0; transform: scale(0.94) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
